/**
 * Traffic sources (/traffic): two tabs.
 *  - Sources: where customers come from and which channels are worth the
 *    time. Totals for the period, the signup/revenue mix, then the whole
 *    whiteboard plan as cards (sub-sources inside their parent's card).
 *  - Website (?tab=website): the website analytics (pages/TrafficPage.tsx),
 *    which used to be its own page at /website (now a redirect).
 */
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Plus, RefreshCw, Route } from "lucide-react";
import { Button, Skeleton, useToast } from "@/components/ui";
import { api } from "@/lib/api";
import { Card, Empty, PageHeader, Segmented, timeAgo } from "@/features/command/ui";
import { SourceCard, type Metric } from "@/features/traffic-sources/SourceCard";
import { MixCard, TotalsStrip } from "@/features/traffic-sources/HubSummary";
import { AddSourceModal } from "@/features/traffic-sources/AddSourceModal";
import { errMsg } from "@/features/traffic-sources/shared";
import type { HubResponse, HubSource, SourceStatus, SyncState } from "@/features/traffic-sources/types";
import { TrafficTabs } from "@/features/traffic-sources/TrafficTabs";
import { CoachCard } from "@/features/coach/CoachCard";

// Charts are heavy: the Website tab loads only when it's opened.
const WebsiteView = lazy(() => import("@/pages/TrafficPage").then((m) => ({ default: m.TrafficPage })));

type Sort = "revenue" | "signups" | "visits" | "plan";
type Filter = "all" | "running" | "later";
const PERIODS = [7, 30, 90] as const;

export function SourcesPage() {
  const [params] = useSearchParams();
  if (params.get("tab") === "website") {
    return (
      <Suspense fallback={<div className="space-y-4"><Skeleton className="h-16 w-72" /><Skeleton className="h-96" /></div>}>
        <WebsiteView tabs={<TrafficTabs active="website" />} />
      </Suspense>
    );
  }
  return <SourcesHub />;
}

function SourcesHub() {
  const toast = useToast();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const days = PERIODS.includes(Number(params.get("days")) as 7) ? Number(params.get("days")) : 30;
  const [data, setData] = useState<HubResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [sort, setSort] = useState<Sort>("revenue");
  const [filter, setFilter] = useState<Filter>("all");
  const [syncing, setSyncing] = useState(false);
  const [adding, setAdding] = useState(false);
  const from = location.pathname + location.search;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.get<HubResponse>("/sources", { days }));
      setFailed(false);
    } catch (e) {
      setFailed(true);
      toast.error("Couldn't load traffic sources", errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [days, toast]);

  useEffect(() => { void load(); }, [load]);

  const setDays = (d: number) => {
    const next = new URLSearchParams(params);
    if (d === 30) next.delete("days"); else next.set("days", String(d));
    setParams(next, { replace: true });
  };

  const changeStatus = async (s: HubSource, status: SourceStatus) => {
    const before = s.status;
    setData((d) => d && { ...d, sources: d.sources.map((x) => (x.id === s.id ? { ...x, status } : x)) });
    try {
      await api.patch(`/sources/${s.id}`, { status });
    } catch (e) {
      setData((d) => d && { ...d, sources: d.sources.map((x) => (x.id === s.id ? { ...x, status: before } : x)) });
      toast.error("Couldn't change the status", errMsg(e));
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post<{ ads: SyncState; social: SyncState }>("/sources/sync");
      if (r.ads?.configured === false && r.social?.configured === false) {
        toast.info("Sync isn't set up here", "This CRM has no SyncSuite key to read ads and followers with.");
      } else {
        const errs = [...(r.ads?.errors ?? []), ...(r.social?.errors ?? [])];
        const bits = [
          `${r.ads?.accounts ?? 0} ad account${r.ads?.accounts === 1 ? "" : "s"}`,
          `${r.social?.accounts ?? 0} social account${r.social?.accounts === 1 ? "" : "s"}`,
        ];
        if (errs.length) toast.error("Synced with problems", errs.slice(0, 3).join(" · "));
        else toast.success("Synced", bits.join(", "));
      }
      await load();
    } catch (e) {
      toast.error("Sync failed", errMsg(e));
    } finally {
      setSyncing(false);
    }
  };

  const view = useMemo(() => {
    if (!data) return null;
    const metric: Metric = sort === "plan" ? "signups" : sort;
    const v = (s: HubSource, k: Metric) => Number(s.n?.[k] ?? 0);
    const order = (a: HubSource, b: HubSource) => {
      if (sort === "plan") return a.position - b.position;
      const keys: Metric[] = sort === "revenue" ? ["revenue", "signups", "visits"] : sort === "signups" ? ["signups", "revenue", "visits"] : ["visits", "signups", "revenue"];
      for (const k of keys) { const d = v(b, k) - v(a, k); if (d) return d; }
      return a.position - b.position;
    };
    const ids = new Set(data.sources.map((s) => s.id));
    const tops = data.sources.filter((s) => !s.parent_id || !ids.has(s.parent_id)).sort(order);
    const kids = new Map<string, HubSource[]>();
    for (const s of data.sources) if (s.parent_id && ids.has(s.parent_id)) kids.set(s.parent_id, [...(kids.get(s.parent_id) ?? []), s]);
    for (const list of kids.values()) list.sort(order);
    const running = (s: HubSource) => s.status === "active" || s.status === "testing";
    const shown = tops.filter((s) => filter === "all" || (filter === "running" ? running(s) : !running(s)));
    const best = Math.max(0, ...tops.map((s) => v(s, metric)));
    const total = Number(data.totals[metric] ?? 0);
    const counts = { all: tops.length, running: tops.filter(running).length, later: tops.filter((s) => !running(s)).length };
    return { tops, kids, shown, best, total, metric, counts };
  }, [data, sort, filter]);

  const lastSync = [data?.sync.ads?.at, data?.sync.social?.at].filter(Boolean).sort().pop() ?? null;

  return (
    <div className="space-y-5">
      <PageHeader
        eyebrow="Growth"
        title="Traffic sources"
        subtitle="Where customers come from, and which channels are worth your time."
        actions={
          <>
            <Segmented size="sm" value={String(days)} onChange={(v) => setDays(Number(v))} options={PERIODS.map((d) => ({ value: String(d), label: `${d} days` }))} />
            <Button
              variant="secondary" size="sm" onClick={sync} loading={syncing}
              leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
              title={lastSync ? `Last synced ${new Date(lastSync).toLocaleString()}` : "Never synced"}
            >
              Sync ads + followers
              <span className="hidden font-normal text-zinc-400 sm:inline">· {lastSync ? timeAgo(lastSync) : "never"}</span>
            </Button>
            <Button size="sm" onClick={() => setAdding(true)} leftIcon={<Plus className="h-3.5 w-3.5" />}>Add a source</Button>
          </>
        }
      />

      <TrafficTabs active="sources" />

      <CoachCard area="overview" />

      {!data && failed ? (
        <Card>
          <Empty icon={Route} title="Couldn't load traffic sources" action={<Button size="sm" variant="secondary" loading={loading} onClick={() => void load()} leftIcon={<RefreshCw className="h-3.5 w-3.5" />}>Try again</Button>}>
            The server didn't answer. Try again in a moment.
          </Empty>
        </Card>
      ) : !data ? (
        <HubSkeleton />
      ) : (
        <div className={loading ? "opacity-70 transition-opacity" : "transition-opacity"}>
          <div className="space-y-4">
            <TotalsStrip data={data} />
            {view && <MixCard tops={view.tops} data={data} />}
          </div>

          <div className="mt-6 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h2 className="whitespace-nowrap text-base font-semibold text-zinc-100">Every source</h2>
              <span className="text-xs text-zinc-500">Faded ones have no numbers yet</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Segmented<Filter> size="sm" value={filter} onChange={setFilter} options={[
                { value: "all", label: `All ${view?.counts.all ?? ""}` },
                { value: "running", label: `Running ${view?.counts.running ?? ""}` },
                { value: "later", label: `Ideas + paused ${view?.counts.later ?? ""}` },
              ]} />
              <Segmented<Sort> size="sm" value={sort} onChange={setSort} options={[
                { value: "revenue", label: "Revenue" },
                { value: "signups", label: "Signups" },
                { value: "visits", label: "Visits" },
                { value: "plan", label: "Plan" },
              ]} />
            </div>
          </div>

          {view && view.shown.length > 0 ? (
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {view.shown.map((s, i) => (
                <SourceCard
                  key={s.id}
                  index={i}
                  source={s}
                  kids={view.kids.get(s.id) ?? []}
                  metric={view.metric}
                  best={view.best}
                  total={view.total}
                  days={days}
                  from={from}
                  onStatus={changeStatus}
                />
              ))}
            </div>
          ) : (
            <Card className="mt-3">
              <Empty
                icon={Route}
                title={data.sources.length ? "Nothing here with that filter" : "No traffic sources yet"}
                action={data.sources.length
                  ? <Button size="sm" variant="secondary" onClick={() => setFilter("all")}>Show all</Button>
                  : <Button size="sm" onClick={() => setAdding(true)} leftIcon={<Plus className="h-3.5 w-3.5" />}>Add a source</Button>}
              >
                {data.sources.length ? "Switch the filter to see the rest of the plan." : "Add the places customers come from (social, ads, partners) and their numbers fill in here."}
              </Empty>
            </Card>
          )}
        </div>
      )}

      <AddSourceModal open={adding} onClose={() => setAdding(false)} sources={data?.sources ?? []} />
    </div>
  );
}

function HubSkeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {Array.from({ length: 5 }, (_, i) => <Skeleton key={i} className="h-[104px] rounded-2xl" />)}
      </div>
      <Skeleton className="h-28 rounded-2xl" />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-56 rounded-2xl" />)}
      </div>
    </div>
  );
}
